"use client";

import type { ExternalAccountID } from "@repo/db/branded-types";
import { useInitialContext, type DefaultInitialContext } from "@repo/sdk";
import { useEffect } from "react";
import { USER_TOKEN_KEYS as KINSA_USER_TOKEN_KEYS } from "../edge/kinsa/constants";
import { USER_TOKEN_KEYS as OWLET_USER_TOKEN_KEYS } from "../edge/owlet/constants";
import { syncUserTokensCookie } from "../edge/user-tokens-cookie";

export const USER_TOKENS_COOKIE_SET_EVENT = "felt-user-tokens-set";

// Build-time inlined via NEXT_PUBLIC_BRAND so only one list survives bundling.
export const TOKEN_KEYS: readonly string[] =
  process.env.NEXT_PUBLIC_BRAND === "owlet"
    ? OWLET_USER_TOKEN_KEYS
    : KINSA_USER_TOKEN_KEYS;

/** True when the cookie is present and contains every brand-required token
 *  key. For idToken specifically, also verifies JWT structure (three dot-
 *  separated segments) so a placeholder or structurally-invalid value from
 *  the native app doesn't prematurely satisfy this check. */
export function hasRequiredUserTokens(
  cookie: Record<string, unknown> | null
): boolean {
  if (!cookie) return false;
  return TOKEN_KEYS.every((key) => {
    const val = cookie[key];
    if (typeof val !== "string" || val === "") return false;
    if (key === "idToken") return val.split(".").length === 3;
    return true;
  });
}

/**
 * Keeps the FELT_USER_TOKENS cookie in sync with the SDK initial context.
 * Mounted in CommonProviders inside a Suspense boundary (same pattern as StatsigObserver).
 *
 * Uses DefaultInitialContext (not a brand-specific type) because this observer
 * lives in @repo/session which is brand-agnostic. Both KinsaInitialContext and
 * OwletInitialContext extend DefaultInitialContext, so account.id and
 * account.custom are available on all brands.
 *
 * On client: useInitialContext suspends until context is available — Suspense catches this.
 * On SSR: the effect is a no-op (no document.cookie).
 */
export function UserTokensObserver() {
  const initialContext = useInitialContext<DefaultInitialContext>();
  // TODO remove once feltbrowser puts these keys in the right place (account.custom)
  const ctxRecord: Record<string, unknown> =
    initialContext != null
      ? (initialContext as unknown as Record<string, unknown>)
      : {};
  const topLevelTokens = Object.fromEntries(
    TOKEN_KEYS.filter((key) => key in ctxRecord).map((key) => [
      key,
      ctxRecord[key],
    ])
  );

  const accountId = initialContext?.account?.id;
  const custom = initialContext?.account?.custom;

  useEffect(() => {
    if (!accountId) return;
    const didSet = syncUserTokensCookie(
      accountId as ExternalAccountID,
      { ...custom, ...topLevelTokens },
      TOKEN_KEYS
    );
    if (didSet) {
      window.dispatchEvent(new Event(USER_TOKENS_COOKIE_SET_EVENT));
    }
  }, [accountId, custom, topLevelTokens]);

  return null;
}
